<template>
  <section class="highlight dark" id="highlight">
    <div class="container">
      <div class="row">
        <div class="col-lg-4 offset-lg-8">
          <div class="card" data-aos="fade-up" :data-tag="t('Most Popular')">
            <div class="price">
              <h3 class="price-name">{{ t('Growth plan') }}</h3>
              <p class="price-amount">
                <span class="price-amount-value">MYR 118</span>
                <span class="price-amount-unit">{{ t('/month') }}</span>
              </p>
              <app-button
                class="price-scroll"
                href="#pricing"
                variant="link"
                tone="light"
                size="lg"
                icon="arrow-right"
                :aria-label="t('See the pricing plans')"
                @click.prevent="scrollToPricing"
              />
            </div>
            <div class="small">{{ t('Great for growing businesses who want a professional image and improve their branding.') }}</div>
          </div>
        </div>
      </div>
      <div class="row gy-4">
        <div class="col-md-4" data-aos="fade-up">
          <font-awesome-icon icon="server" />
          <h3>{{ t('Free hosting and domain') }}</h3>
          <p>{{ t("We provide scalable web hosting and top-level domain name for your website as long as you're part of our big family.") }}</p>
        </div>
        <div class="col-md-4" data-aos="fade-up">
          <font-awesome-icon icon="award" />
          <h3>{{ t('Free SSL certificate') }}</h3>
          <p>{{ t('We provide SSL certificate and make sure that your website is secured, your security and privacy as a right should never be charged.') }}</p>
        </div>
        <div class="col-md-4" data-aos="fade-up">
          <font-awesome-icon icon="envelope" />
          <h3>{{ t('Free business emails') }}</h3>
          <p>{{ t('We provide multiple business emails that use your custom domain to give all your work emails a professional makeover.') }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.highlight {
  padding-bottom: 2.5rem;

  .row:nth-child(1) {
    .card {
      border-radius: $r-xs;
      box-shadow: $shadow-sm;
      margin-bottom: 3rem;
      margin-top: -15rem;
      padding: 4rem 1.25rem 1.25rem;
      min-height: 225px;
      transition: box-shadow $dur $ease, transform $dur $ease;

      /* The card floats on the dark band below the hero, so a lift here reads
         as it coming forward rather than shifting along with the page. A plain
         dark shadow barely shows on dark ground, so it carries a faint blue
         ring as well. */
      &:hover {
        box-shadow: $shadow-dark, 0 0 0 1px $border-primary;
        transform: translateY(-4px);

        /* The card is the hover target, so the arrow can answer a hover
           anywhere on it rather than only on the arrow itself. The card lifts
           and the arrow takes the accent; the arrow itself does not move, which
           is the one rule every button on the site now follows. */
        .price .price-scroll {
          color: $primary;
        }
      }

      &::before {
        background: $secondary;
        border-radius: $r-xs;
        box-shadow: 0 0.25rem 0.5rem rgba($secondary, .5);
        color: $on-dark-strong;
        content: attr(data-tag);
        font-size: 0.85rem;
        font-weight: 500;
        left: -1rem;
        padding: 0.25rem 0.75rem;
        position: absolute;
        top: 1rem;
      }

      &::after {
        background: $ground;
        border-radius: $r-xs;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        content: '';
        height: 0.75rem;
        left: 0.75rem;
        opacity: 0.25;
        position: absolute;
        top: 100%;
        width: calc(100% - 1.5rem);
      }

      .price {
        position: relative;

        /* A scroll control down to the pricing table. Its ink, its size and its
           nudge are AppButton's `link` variant at size `lg`, so only the
           placement stays here. */
        .price-scroll {
          position: absolute;
          right: 0.5rem;
          top: calc(50% - 24px);
        }
      }

      /* The card is its own light surface inside the dark band, so its headings
         take the light-ground ink rather than the white the band sets. The plan
         name is the card's title and the price is a figure, not a heading, so
         it is a paragraph and the page outline stays about the plans. */
      h3 {
        color: $text-heading;
      }

      .price-name {
        font-size: 1.5rem;
        margin-bottom: 0.5rem;
      }

      .price-amount {
        align-items: baseline;
        display: flex;
        gap: 0.5rem;
        margin-bottom: 1rem;
      }

      .price-amount-value {
        color: $primary;
        font-family: $headings-font-family;
        font-size: 2rem;
        font-weight: $headings-font-weight;
        line-height: 1.2;
      }

      .price-amount-unit {
        font-size: 1rem;
      }

      @media (max-width: 991.98px) {
        min-height: unset;
      }
    }
  }

  .row:nth-child(2) {
    div[class^='col-'] {
      svg {
        align-items: center;
        border-radius: $r-lg;
        border: solid 1px $on-dark-line-strong;
        color: $on-dark-strong;
        display: flex;
        height: 60px;
        justify-content: center;
        margin-bottom: 1.5rem;
        padding: 1rem;
        position: relative;
        transition: background-color $dur $ease, border-color $dur $ease, transform $dur $ease;
        width: 60px;
        z-index: 10;
      }

      /* Same outlined-circle treatment the feature tiles get, one size down. */
      &:hover {
        svg {
          background: $on-dark-line;
          border-color: $on-dark-body;
          transform: translateY(-3px);
        }
      }
    }
  }
}
</style>

<script>
import { useI18n } from 'vue-i18n';
import { scrollToId } from '@/smoothScroll';
import AppButton from '@/Components/AppButton.vue';

export default {
  components: {
    AppButton
  },

  methods: {
    scrollToPricing() {
      scrollToId('pricing');
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          '/month': '/ month',
          'Free business emails': 'Free business emails',
          'Free hosting and domain': 'Free hosting and domain',
          'Free SSL certificate': 'Free SSL certificate',
          'See the pricing plans': 'See the pricing plans',
          'We provide multiple business emails that use your custom domain to give all your work emails a professional makeover.': 'We provide multiple business emails that use your custom domain to give all your work emails a professional makeover.',
          'We provide SSL certificate and make sure that your website is secured, your security and privacy as a right should never be charged.': 'We provide SSL certificate and make sure that your website is secured, your security and privacy as a right should never be charged.',
          "We provide scalable web hosting and top-level domain name for your website as long as you're part of our big family.": "We provide scalable web hosting and top-level domain name for your website as long as you're part of our big family."
        },
        'zh-MY': {
          '/month': '/ 每月',
          'Free business emails': '免费商务电子邮箱',
          'Free hosting and domain': '免费网络托管和顶级域名',
          'Free SSL certificate': '免费SSL认证',
          'See the pricing plans': '查看收费配套',
          'We provide multiple business emails that use your custom domain to give all your work emails a professional makeover.': '我们将提供您多个可客制化的商务电子邮箱。这些商业邮箱将使用您的顶级域名，以确保您的工作电邮无时无刻都保持专业。',
          'We provide SSL certificate and make sure that your website is secured, your security and privacy as a right should never be charged.': '我们将提供您SSL认证，并且时刻确保您的网站受到保护。您的安全及隐私本是您的基本的权力，并不应该沦为一种商业消费。',
          "We provide scalable web hosting and top-level domain name for your website as long as you're part of our big family.": '我们将提供您可扩展式虚拟伺服器以及顶级域名，只要您持续订阅我们并成为我们大家庭的一员，即可享受永久的网络托管与域名服务。'
        },
        'ms-MY': {
          '/month': '/ bulan',
          'Free business emails': 'E-mel perniagaan percuma',
          'Free hosting and domain': 'Hosting dan domain percuma',
          'Free SSL certificate': 'Sijil SSL percuma',
          'See the pricing plans': 'Lihat pelan harga',
          'We provide multiple business emails that use your custom domain to give all your work emails a professional makeover.': 'Kami membekalkan e-mel business yang menggunakan domain anda untuk meningkatkan imej professional anda.',
          'We provide SSL certificate and make sure that your website is secured, your security and privacy as a right should never be charged.': 'Kami membekalkan sijil SSL dan memastikan keselamatan website anda terjamin. Keselamatan dan privasi adalah hak anda.',
          "We provide scalable web hosting and top-level domain name for your website as long as you're part of our big family.": 'Kami membekalkan hosting web dan domain peringkat atasan selagi anda sebahagian daripada keluarga besar kami.'
        }
      }
    });

    return { t };
  }
};
</script>
